import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './MessageManagement.css';

const MessageManagement = () => {
  const [messages, setMessages] = useState({
    aguardandoDriver: '',
    aguardandoPartner: '',
    descargaDriver: '',
    descargaPartner: '',
    concluidoDriver: '',
    concluidoPartner: '',
    adminSummary: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch current messages on component mount
  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      // For now, we'll use default messages since there's no backend endpoint
      // In a real implementation, this would fetch from the backend
      setMessages({
        aguardandoDriver: `🚛 *NOTIFICAÇÃO DE CHEGADA*

Olá {{driverName}},

Seu caminhão chegou ao pátio para descarga.

📅 Data/Hora: {{entryTime}}
🏷️ Placa: {{trailerPlate}}
🏢 Parceiro: {{partnerName}}

Aguarde a liberação para iniciar a descarga.`,
        
        aguardandoPartner: `🚛 *NOTIFICAÇÃO DE CHEGADA DE CAMINHÃO*

Olá {{partnerName}},

Um caminhão seu chegou ao pátio da NBL LOG para descarga.

📅 Data/Hora: {{entryTime}}
🏷️ Placa: {{trailerPlate}}
👷 Motorista: {{driverName}}

Aguardando liberação para iniciar a descarga.`,
        
        descargaDriver: `🚛 *INÍCIO DA DESCARGA*

Olá {{driverName}},

Sua descarga foi iniciada.

📅 Data/Hora: {{startTime}}
🏷️ Placa: {{trailerPlate}}
🏢 Parceiro: {{partnerName}}
👷 Conferente: {{checkerName}}

Acompanhe o processo de descarga.`,
        
        descargaPartner: `🚛 *INÍCIO DA DESCARGA DE CAMINHÃO*

Olá {{partnerName}},

A descarga de um caminhão seu foi iniciada no pátio da NBL LOG.

📅 Data/Hora: {{startTime}}
🏷️ Placa: {{trailerPlate}}
👷 Motorista: {{driverName}}
👷 Conferente: {{checkerName}}

Acompanhe o processo de descarga.`,
        
        concluidoDriver: `✅ *DESCARGA CONCLUÍDA*

Olá {{driverName}},

Sua descarga foi concluída com sucesso.

📅 Data/Hora: {{endTime}}
🏷️ Placa: {{trailerPlate}}
🏢 Parceiro: {{partnerName}}

Você já pode retirar seu caminhão do pátio.`,
        
        concluidoPartner: `✅ *DESCARGA CONCLUÍDA DE CAMINHÃO*

Olá {{partnerName}},

A descarga de um caminhão seu foi concluída com sucesso no pátio da NBL LOG.

📅 Data/Hora: {{endTime}}
🏷️ Placa: {{trailerPlate}}
👷 Motorista: {{driverName}}

O caminhão já pode ser retirado do pátio.`,
        
        adminSummary: `🚛 *RESUMO DE DESCARGAS*

Uma alteração de status ocorreu: *{{statusChanged}}*

Placa: {{trailerPlate}}
Parceiro: {{partnerName}}
Horário: {{currentTime}}

📊 *RESUMO ATUAL:*
🔴 Aguardando: {{aguardandoCount}}
🟡 Em Descarga: {{emDescargaCount}}
🟢 Concluídas: {{concluidoCount}}
🔢 Total: {{totalCount}}`
      });
      setError('');
    } catch (err) {
      setError('Erro ao carregar mensagens: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessages({
      ...messages,
      [name]: value
    });
  };

  const handleSaveMessages = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // In a real implementation, this would save to the backend
      // For now, we'll just show a success message
      setSuccess('Mensagens salvas com sucesso!');
      setError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('Erro ao salvar mensagens: ' + (err.response?.data?.message || err.message));
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="message-management-container">
      <h2>Gerenciar Mensagens de Notificação</h2>
      <p className="description">
        Edite as mensagens que são enviadas automaticamente aos motoristas, parceiros e administradores 
        quando ocorrem alterações de status nos caminhões.
      </p>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSaveMessages} className="messages-form">
        <div className="message-section">
          <h3>Notificação de Chegada (Motorista)</h3>
          <div className="form-group">
            <textarea
              name="aguardandoDriver"
              value={messages.aguardandoDriver}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o motorista quando o caminhão chega"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{driverName}'}, {'{entryTime}'}, {'{trailerPlate}'}, {'{partnerName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Notificação de Chegada (Parceiro)</h3>
          <div className="form-group">
            <textarea
              name="aguardandoPartner"
              value={messages.aguardandoPartner}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o parceiro quando o caminhão chega"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{partnerName}'}, {'{entryTime}'}, {'{trailerPlate}'}, {'{driverName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Notificação de Início de Descarga (Motorista)</h3>
          <div className="form-group">
            <textarea
              name="descargaDriver"
              value={messages.descargaDriver}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o motorista quando a descarga inicia"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{driverName}'}, {'{startTime}'}, {'{trailerPlate}'}, {'{partnerName}'}, {'{checkerName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Notificação de Início de Descarga (Parceiro)</h3>
          <div className="form-group">
            <textarea
              name="descargaPartner"
              value={messages.descargaPartner}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o parceiro quando a descarga inicia"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{partnerName}'}, {'{startTime}'}, {'{trailerPlate}'}, {'{driverName}'}, {'{checkerName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Notificação de Descarga Concluída (Motorista)</h3>
          <div className="form-group">
            <textarea
              name="concluidoDriver"
              value={messages.concluidoDriver}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o motorista quando a descarga é concluída"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{driverName}'}, {'{endTime}'}, {'{trailerPlate}'}, {'{partnerName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Notificação de Descarga Concluída (Parceiro)</h3>
          <div className="form-group">
            <textarea
              name="concluidoPartner"
              value={messages.concluidoPartner}
              onChange={handleInputChange}
              rows="8"
              disabled={loading}
              placeholder="Mensagem para o parceiro quando a descarga é concluída"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>{'{partnerName}'}, {'{endTime}'}, {'{trailerPlate}'}, {'{driverName}'}</span>
            </div>
          </div>
        </div>
        
        <div className="message-section">
          <h3>Resumo para Administradores</h3>
          <div className="form-group">
            <textarea
              name="adminSummary"
              value={messages.adminSummary}
              onChange={handleInputChange}
              rows="12"
              disabled={loading}
              placeholder="Mensagem de resumo enviada aos administradores"
            />
            <div className="message-variables">
              <strong>Variáveis disponíveis:</strong>
              <span>
                {'{statusChanged}'}, {'{trailerPlate}'}, {'{partnerName}'}, {'{currentTime}'},
                {'{aguardandoCount}'}, {'{emDescargaCount}'}, {'{concluidoCount}'}, {'{totalCount}'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-save"
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Todas as Mensagens'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageManagement;